/**
 * SpeakerBindingBanner Component
 *
 * Banner displayed on the reflection detail page when speaker binding
 * has not been confirmed by the user. Provides a quick action to open
 * the speaker binding wizard.
 */

import { AlertTriangle, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { BindingStatus } from "@/types/transcript";

interface SpeakerBindingBannerProps {
  bindingStatus: BindingStatus | null;
  onReviewSpeakers: () => void;
  onDismiss?: () => void;
  className?: string;
}

export function SpeakerBindingBanner({
  bindingStatus,
  onReviewSpeakers,
  onDismiss,
  className = "",
}: SpeakerBindingBannerProps) {
  // Only show banner for statuses that need attention
  if (
    !bindingStatus ||
    bindingStatus === "user_confirmed" ||
    bindingStatus === "auto_bound"
  ) {
    return null;
  }

  const getMessage = () => {
    switch (bindingStatus) {
      case "pending":
        return "Speaker identification is pending. Help us identify who's who in this conversation.";
      case "needs_review":
        return "We detected speakers but couldn't confidently identify them. Please review.";
      default:
        return "Please confirm speaker identities for accurate feedback.";
    }
  };

  return (
    <div
      className={`rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 p-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-amber-900 dark:text-amber-100">
            Identify Speakers
          </h3>
          <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
            {getMessage()}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900 text-amber-900 dark:text-amber-100"
            onClick={onReviewSpeakers}
          >
            <Users className="h-4 w-4 mr-2" />
            Review Speakers
          </Button>
        </div>
        {onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            className="flex-shrink-0 h-8 w-8 p-0 text-amber-600 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900"
            onClick={onDismiss}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Dismiss</span>
          </Button>
        )}
      </div>
    </div>
  );
}
